// Picks the inline editor for a custom field's type. CustomFieldEditorProps is the contract every one of
// the editors implements.
import React from 'react';
import type {CustomFieldColumnDef} from '../../../customFieldColumnModel';
import type {OpportunityNode} from '../../../opportunityTypes';
import {TextFieldEditor} from './TextFieldEditor';
import {NumberFieldEditor} from './NumberFieldEditor';
import {DateFieldEditor} from './DateFieldEditor';
import {DropdownFieldEditor} from './DropdownFieldEditor';

// Localized copy, resolved once per page render.
export interface CustomFieldEditorLabels {
	update: string;
	cancel: string;
	none: string;
	integerInvalid: string;
	decimalInvalid: string;
}

export interface CustomFieldEditorProps {
	node: OpportunityNode;
	def: CustomFieldColumnDef;
	// The stored value in its stored form (a dropdown's is the option key, a date's is ISO), or null.
	current: string | null;
	// The page's retry(): called once a save succeeds, so the cell shows the new value after the refetch.
	onCommit: () => void;
	// Dismisses the popover.
	close: () => void;
	labels: CustomFieldEditorLabels;
	// The viewer's decimal separator, from their profile language (decimalSeparatorFor). A decimal is
	// shown and typed with it and stored with '.', as on the record page.
	decimalSeparator: ',' | '.';
}

export const CustomFieldEditor = (props: CustomFieldEditorProps): JSX.Element => {
	switch (props.def.kind) {
		case 'DROPDOWN':
			return <DropdownFieldEditor {...props} />;
		case 'INTEGER':
		case 'DECIMAL':
			return <NumberFieldEditor {...props} />;
		case 'DATE':
			return <DateFieldEditor {...props} />;
		default:
			return <TextFieldEditor {...props} />;
	}
};

export default CustomFieldEditor;
